import { ApplicationApi } from 'api';
import { cacheTime, infinityCacheTime } from 'api/ReactQueryOptions';
import { V1 } from 'api/v1/V1';

export type HealthCheckActivitiesQueryParams = Parameters<
	InstanceType<typeof V1>['getServiceContent']
>[0];

export const getHealthCheckActivitiesQuery = (
	api: ApplicationApi,
	publicUnitId: string
) => ({
	queryKey: healthCheckKeys.activities(publicUnitId),
	queryFn: async () => (await api.getActivityNames({ publicUnitId })).data,
});

export const getHealthCheckListQuery = (
	api: ApplicationApi,
	unitPublicId: string
) => ({
	queryKey: healthCheckKeys.list(unitPublicId),
	queryFn: async () => (await api.getHealthChecks({ unitPublicId })).data,
});

export const getHealthCheckQuery = (api: ApplicationApi, id: number) => ({
	queryKey: healthCheckKeys.single(id),
	queryFn: async () => (await api.getHealthCheck(id)).data,
});

export const getSupportedProtocolLanguagesQuery = (api: ApplicationApi) => ({
	queryKey: healthCheckKeys.languages(),
	queryFn: async () => {
		const response = await api.getSupportedProtocolLanguageList();
		return response.data;
	},
	...infinityCacheTime<Record<string, string>>(),
});

export const healthCheckFilesQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: healthCheckKeys.files(unitId),
	queryFn: async () => (await api.getHealthCheckFiles(unitId)).data,
	...cacheTime(5),
	retry: 0,
});

export const healthCheckKeys = {
	all: ['healthCheck'] as const,

	activities: (publicUnitId: string) =>
		[...healthCheckKeys.all, 'activities', publicUnitId] as const,
	list: (unitPublicId: string) =>
		[...healthCheckKeys.all, 'list', unitPublicId] as const,
	single: (id: number) => [...healthCheckKeys.all, id] as const,
	languages: () => [...healthCheckKeys.all, 'languages'] as const,
	files: (unitId: number) =>
		[...healthCheckKeys.all, 'files', unitId] as const,
};
